import slugify from 'slugify'
import { toFixedNumber, createTitle } from '~/utils'
import { getMaxQtyFallback, capStock } from '~/utils/stock'
import type {
  ListingSchemaProduct,
  ProductSearchItem,
  GetPricesHireData,
  ExtrasItem,
  PriceValue,
  LunarProductPreBasket,
  LineItemProps,
  ExtrasProduct,
  ItemTotals,
  ProductDetailsVariantProps,
  ProductStringAttribute,
  ProductDetailsProps,
  HirePeriodTooltip,
} from '~/types'

// Returns the pickup points array
export const mapPickupPoints = (
  pickupPoints?: GetPricesHireData['pickupPoints']
) => {
  return pickupPoints && pickupPoints.length !== 0 ? pickupPoints : []
}

/**
 * Computes the inc-VAT product price from itemTotals.
 * Hire (bulk): hireTotal + hireVat. Buy (consumable): resaleTotal + resaleVat.
 * If productType is omitted, it's inferred from the presence of hireTotal.
 *
 * For main items the totals are already quantity-adjusted.
 * For linked/cross-sell products (extras modal) they're per-unit and need
 * multiplying by the selected quantity.
 */
export const getProductPrice = (
  totals: ProductPriceTotals | null | undefined,
  productType?: string
): PriceValue | null => {
  if (!totals) return null

  const isHire =
    productType === 'bulk' ||
    (productType == null && totals.hireTotal?.amount != null)

  if (isHire) {
    const total = totals.hireTotal?.amount
    const vat = totals.hireVat?.amount
    if (total == null || vat == null) return null
    return {
      currency: totals.hireTotal?.currency ?? 'GBP',
      amount: toFixedNumber(total + vat, 2),
    }
  }

  const total = totals.resaleTotal?.amount
  const vat = totals.resaleVat?.amount
  if (total == null || vat == null) return null
  return {
    currency: totals.resaleTotal?.currency ?? 'GBP',
    amount: toFixedNumber(total + vat, 2),
  }
}

export type ProductPriceTotals = {
  hireTotal?: { currency: string; amount: number } | null
  hireVat?: { currency: string; amount: number } | null
  resaleTotal?: { currency: string; amount: number } | null
  resaleVat?: { currency: string; amount: number } | null
}

/**
 * Derives the full delivery price (inc VAT) from itemTotals.
 * Uses transportTotal + transportVat which includes both delivery
 * and collection for hire products.
 */
export const getDeliveryPrice = (totals: ItemTotals): PriceValue | null => {
  if (totals?.transportTotal?.amount == null) return null

  return {
    currency: totals.transportTotal.currency ?? 'GBP',
    amount: toFixedNumber(
      (totals.transportTotal.amount ?? 0) + (totals.transportVat?.amount ?? 0),
      2
    ),
  }
}

// Map the cross sells data so we have a uniform set of data
export const mapExtras = (
  productType: 'consumable' | 'bulk',
  extraType: 'addOn' | 'crossSell',
  products?: ExtrasItem[]
): ExtrasProduct[] => {
  return products && products.length !== 0
    ? products
        .map(e => {
          // Price from itemTotals (per-unit for linked/cross-sell products)
          const unitPrice = getProductPrice(e.itemTotals, productType)
          const deliveryTotal = getDeliveryPrice(e.itemTotals)

          const noPrice = {
            amount: 0,
            currency: 'GBP',
          }

          const priceToUse = unitPrice?.amount ? unitPrice : noPrice

          // Use delivery stock from siteWithStock if available, otherwise fallback
          const fallbackMax = getMaxQtyFallback(productType)
          const maxQuantity = capStock(
            e?.siteWithStock?.maxQuantity,
            fallbackMax
          )

          return {
            title: e.product.name,
            image: e.product.image,
            stockCode: e.product.stockCode?.toString(),
            price: priceToUse,
            productType: productType,
            extraType: extraType,
            maxQuantity,
            minQuantity: 0,
            damageWaiver: e?.damageWaiver ?? 0,
            deliveryDate: e?.deliveryDate
              ? e.deliveryDate
              : e?.firstAvailableDate
                ? e.firstAvailableDate
                : null,
            deliveryPrice: deliveryTotal ?? noPrice,
            deliveryStock:
              typeof e?.siteWithStock?.maxQuantity === 'number'
                ? e.siteWithStock.maxQuantity
                : null,
            firstAvailableDate: e?.firstAvailableDate ?? null,
            isMandatory: e?.isMandatory ? true : false,
            requiresFraudCheck: e?.requiresFraudCheck ? true : false,
          }
        })
        .filter(e => e?.price?.amount)
    : []
}

// Maps our data keys to the ones required for Lunar basket
/**
 * Builds the variant descriptor shown under a basket line title
 * ("Size: 9.5M") from Brenda's variant `option` pair.
 *
 * Products with no real variant still come back with a placeholder
 * `{ name: 'Default', value: 'default' }`, which would otherwise render
 * as "Default: default" on every such line. Both halves must be present
 * and neither may be that placeholder.
 */
export const buildVariantOption = (
  option?: { name?: string | null; value?: string | null } | null
): string | undefined => {
  const name = option?.name?.trim()
  const value = option?.value?.trim()

  if (!name || !value) return undefined

  const isPlaceholder = [name, value].some(e => e.toLowerCase() === 'default')

  if (isPlaceholder) return undefined

  return `${name}: ${value}`
}

const basketKeyMap = [
  {
    name: 'productType',
    basketKey: 'product_type',
  },
  {
    name: 'deliveryPostcode',
    basketKey: 'delivery_postcode',
  },
  {
    name: 'deliveryPrice',
    basketKey: 'delivery_price',
  },
  {
    name: 'deliveryDate',
    basketKey: 'delivery_date',
  },
  {
    name: 'fulfilment',
    basketKey: 'selected_fulfilment',
  },
  {
    name: 'pickupBranchId',
    basketKey: 'pickup_branch_id',
  },
  {
    name: 'pickupBranchAddress',
    basketKey: 'pickup_branch_address',
  },
  {
    name: 'pickupBranchDistance',
    basketKey: 'pickup_branch_distance',
  },
  {
    name: 'pickupDate',
    basketKey: 'pickup_date',
  },
  {
    name: 'hireDeposit',
    basketKey: 'hire_deposit',
  },
  {
    name: 'hireDamageWaiver',
    basketKey: 'hire_damage_waiver',
  },
  {
    name: 'hireDaysRequested',
    basketKey: 'hire_days_requested',
  },
  {
    name: 'hireStartDateRequested',
    basketKey: 'hire_start_date_requested',
  },
  {
    name: 'hireStartDate',
    basketKey: 'hire_start_date',
  },
  {
    name: 'hireEndDate',
    basketKey: 'hire_end_date',
  },
  {
    name: 'lineGroupId',
    basketKey: 'line_group_id',
  },
  {
    name: 'lineParentGroupId',
    basketKey: 'line_parent_group_id',
  },
  {
    name: 'isMandatory',
    basketKey: 'is_mandatory',
  },
  {
    name: 'requiresFraudCheck',
    basketKey: 'requires_fraud_check',
  },
  {
    name: 'category',
    basketKey: 'category',
  },
  {
    name: 'title',
    basketKey: 'title',
  },
  {
    name: 'variantOption',
    basketKey: 'variant_option',
  },
  {
    name: 'maxQuantity',
    basketKey: 'max_quantity',
  },
]

// Date meta keys — skip these if the value is empty/null to avoid sending invalid data
const dateKeys = new Set([
  'delivery_date',
  'pickup_date',
  'hire_start_date',
  'hire_end_date',
  'hire_start_date_requested',
])

export const mapBasketItem = (
  product: LunarProductPreBasket
): LineItemProps => {
  let meta = {
    price: product?.price,
  } as LineItemProps['meta']

  if (product?.title) {
    meta = { ...meta, title: product.title }
  }

  if (product.category) {
    meta = { ...meta, category: product.category }
  }

  for (const [key, value] of Object.entries(product)) {
    const mappedItem = basketKeyMap.find(x => x.name === key)

    if (mappedItem) {
      // Skip empty date values — Lunar rejects null/empty dates
      if (dateKeys.has(mappedItem.basketKey) && !value) continue

      Object.assign(meta, { [mappedItem.basketKey]: value })
    }
  }

  return {
    quantity: product.quantity,
    stock_code: product.stockCode,
    meta: meta,
  }
}

// Gets the links for PDP pages of products
export const getProductPageLink = ({
  productType,
  stockCode,
  slug,
  variant,
}: {
  productType: string
  stockCode: string
  slug: string
  variant?: string
}): string => {
  let url = `/${productType === 'bulk' ? 'hire' : 'buy'}/p/${stockCode}/${slug}`

  if (variant && variant !== 'default') {
    url = `${url}?variant=${slugify(variant)}`
  }

  return encodeURI(url.toLowerCase())
}

// Check if string is a valid URL
// Alternative to URL.canParse (not supported in older node versions)
const isUrlValid = (string: string) => {
  try {
    new URL(string)
    return true
  } catch (err) {
    return false
  }
}

// https://developers.google.com/youtube/player_parameters#Manual_IFrame_Embeds
// https://developers.google.com/youtube/player_parameters#Manual_IFrame_Embeds
export const youtubeUrlToEmbed = (
  urlString: string | undefined | null,
  autoplay: boolean = false
): string | null | undefined => {
  const template = (v: string) => {
    const baseUrl = `https://www.youtube.com/embed/${v}`
    if (autoplay) {
      // Add required parameters for autoplay to work in modern browsers
      return `${baseUrl}?autoplay=1&mute=1&playsinline=1`
    }
    return baseUrl
  }

  if (urlString && isUrlValid(urlString)) {
    // Full URL
    const url = new URL(urlString)

    // short URL
    if (url.hostname === 'www.youtu.be' || url.hostname === 'youtu.be') {
      return template(
        url.pathname.startsWith('/') ? url.pathname.substring(1) : url.pathname
      )
    }

    // regular URL
    const v = url.searchParams.get('v')

    if (
      (url.hostname === 'www.youtube.com' || url.hostname === 'youtube.com') &&
      v
    ) {
      return template(v)
    }
  }

  return urlString
}

export const getVimeoEmbedUrl = (
  vimeoUrl: string,
  autoplay: boolean = false
): string => {
  // Match either of the following:
  // - /manage/videos/VIDEO_ID/HASH (optional hash)
  // - /VIDEO_ID/HASH (optional hash)
  // - /manage/videos/VIDEO_ID
  // - /VIDEO_ID
  const regex = /(?:videos\/)?(\d+)(?:\/([a-zA-Z0-9]+))?/
  const match = vimeoUrl.match(regex)

  if (!match) return ''

  const videoId: string = match[1]
  const hash: string | undefined = match[2]

  // Build the base URL
  const embedUrl = `https://player.vimeo.com/video/${videoId}`

  // Add parameters
  const params = new URLSearchParams()
  if (hash) {
    params.append('h', hash)
  }
  if (autoplay) {
    // Add required parameters for autoplay to work in modern browsers
    params.append('autoplay', '1')
    params.append('muted', '1')
    params.append('playsinline', '1')
  }

  // Only add query string if we have parameters
  return params.toString() ? `${embedUrl}?${params.toString()}` : embedUrl
}

// Helper function to detect video platform
const detectVideoPlatform = (url: string): 'youtube' | 'vimeo' | 'unknown' => {
  try {
    const urlObj = new URL(url)
    const hostname = urlObj.hostname.toLowerCase()

    // YouTube detection
    if (
      hostname === 'www.youtube.com' ||
      hostname === 'youtube.com' ||
      hostname === 'youtu.be' ||
      hostname === 'www.youtu.be'
    ) {
      return 'youtube'
    }

    // Vimeo detection
    if (hostname === 'vimeo.com' || hostname === 'www.vimeo.com') {
      return 'vimeo'
    }

    return 'unknown'
  } catch {
    return 'unknown'
  }
}

// Unified function to get embed URL for any supported platform
export const getVideoEmbedUrl = (
  url: string,
  autoplay: boolean = false
): string => {
  if (!url) return ''

  const platform = detectVideoPlatform(url)

  switch (platform) {
    case 'youtube':
      return youtubeUrlToEmbed(url, autoplay) || ''

    case 'vimeo': {
      const embedUrl = getVimeoEmbedUrl(url, autoplay)
      return embedUrl || ''
    }

    case 'unknown':
    default:
      return ''
  }
}

export const getVariantPrices = (e: ProductDetailsVariantProps) => {
  return {
    variantGuidePrice:
      e?.guidePrice?.amount && e?.guidePrice?.currency
        ? {
            currency: e.guidePrice.currency,
            amount: e.guidePrice.amount,
          }
        : null,
    variantSalePrice:
      e?.salePrice?.amount && e?.salePrice?.currency
        ? {
            currency: e.salePrice.currency,
            amount: e.salePrice.amount,
          }
        : null,
  }
}

export const getMatchingVariant = (
  variants: ProductDetailsVariantProps[],
  selectedVariant: string
) => {
  return variants.find((e: ProductDetailsVariantProps) => {
    const variantValue = e?.option?.value
      ? slugify(e.option.value.toLowerCase())
      : null
    const urlValue = selectedVariant
      ? slugify(decodeURI(selectedVariant))
      : null

    if (variantValue === urlValue) {
      return true
    } else {
      return false
    }
  })
}

export const createProductCategoryTitles = (product: ProductDetailsProps) => {
  // Get the URL category value
  const categoryPath = product?.stringAttributes.find(
    (e: ProductStringAttribute) => e.name === 'categories'
  )

  // Split the URL and convert them to titles
  const categoryNames = categoryPath
    ? categoryPath?.value?.split('/').map(e => createTitle(e))
    : []

  return categoryNames?.length === 2 ? categoryNames : null
}

/*
  Reduces Brenda's inconsistently cased and spaced safety slugs to one key.
*/
export const normaliseSafetyIcon = (icon: string) =>
  icon
    ?.toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .replace(/icon$/, '') ?? ''

/**
 * Picks the hire-period tooltip whose day band contains the selected duration.
 *
 * Bands are sorted shortest-first, so an overlapping pair (2-7 and 7+) resolves
 * to the narrower band rather than depending on the editor's drag order in
 * Sanity. A band with no `maxDays` is open-ended. Returns null when nothing
 * matches, which leaves the hire-period message with no tooltip.
 */
export const resolveHirePeriodTooltip = (
  tooltips: HirePeriodTooltip[] | undefined,
  days: number
): HirePeriodTooltip | null => {
  const match = [...(tooltips ?? [])]
    .sort((a, b) => a.minDays - b.minDays)
    .find(
      band =>
        days >= band.minDays && (band.maxDays == null || days <= band.maxDays)
    )

  return match ?? null
}

/**
 * Maps resolved listing products into the CollectionPage ItemList shape.
 *
 * Lifted out of the page component so a listing emits JSON-LD only, matching
 * the PDP, rather than microdata competing with a second copy on every card.
 */
export const buildListingSchemaProducts = (
  products: ProductSearchItem[]
): ListingSchemaProduct[] =>
  products.map(product => {
    const amount = product.salePrice?.amount ?? product.guidePrice?.amount

    return {
      title: product.name,
      slug: getProductPageLink({
        productType: product.productType,
        stockCode: product.url?.stockCode,
        slug: product.url?.slug,
        variant: product.url?.option?.value,
      }),
      image: product.image?.imageUrl ?? null,
      sku: product.variantStockCode ?? product.url?.stockCode ?? null,
      price: typeof amount === 'number' ? amount.toFixed(2) : null,
      currency:
        product.salePrice?.currency ?? product.guidePrice?.currency ?? 'GBP',
    }
  })
